<template>
    <div class="deletion-guide-tab-container">
        <SharedSpinner v-if="loading" label="Loading deletion guide..." />
        <template v-else>
            <DeletionGuideAlert />

            <!-- Deletion Guide Content -->
            <div v-if="deletionGuide" v-html="deletionGuide" class="deletion-guide-content" />
            <NoDeletionGuide v-else />
        </template>
        <FeatureLockedModal
            v-model="featureModalOpen"
            :mode="featureModalMode"
            feature="Exposure Deletion Guides"
        />
    </div>
</template>

<script setup lang="ts">
import NoDeletionGuide from '~/components/main/person/companies/CompanyView/CompanyTabs/DeletionGuideTab/NoDeletionGuide.vue';
import { companiesService } from '~/services/companies-service';
import { useExposureSidebarStore } from '~/stores/components/modals/exposure-sidebar';
import DeletionGuideAlert from '../CompanySideBar/DeletionGuideAlert.vue';

const exposureStore = useExposureSidebarStore();
const { company } = storeToRefs(exposureStore);

const deletionGuide = ref<string | null>(null);
const loading = ref(false);
const lastFetchedCompanyId = ref<number | null>(null);

const { featureModalOpen, featureModalMode } = useFeatureLockedModal();

const loadDeletionGuide = async () => {
    if (!company.value?.id) {
        deletionGuide.value = null;
        loading.value = false;
        return;
    }

    // Only fetch if we haven't already fetched for this company
    if (lastFetchedCompanyId.value === company.value.id) {
        return;
    }

    try {
        loading.value = true;
        lastFetchedCompanyId.value = company.value.id;
        deletionGuide.value = await companiesService.getDeletionGuide(company.value.id);
    } catch (error) {
        console.error('Failed to fetch deletion guide:', error);
        deletionGuide.value = null;
    } finally {
        loading.value = false;
    }
};

watch(() => company.value?.id, (newId) => {
    if (newId && newId !== lastFetchedCompanyId.value) {
        loadDeletionGuide();
    }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.deletion-guide-tab-container {
    @apply flex flex-col;
}

.deletion-guide-content {
    @apply bg-gray-50/50 gap-4 flex flex-col font-inter text-deletion-text px-6 pt-6;

    :deep(p) {
        @apply mb-4 text-deletion-text leading-relaxed font-inter;
    }

    :deep(h3) {
        @apply text-deletion-text;
    }
    
    :deep(strong) {
        @apply font-semibold text-deletion-text;
    }
    
    :deep(ul) {
        @apply mb-4 pl-6 list-disc;
    }
    :deep(ol) {
        @apply mb-4 pl-6 list-decimal;
    }
    
    :deep(li) {
        @apply mb-2 text-deletion-text;
    }
}
</style>
